import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { demeterUserDataApi } from '../../../Apis/Apis';
import type { DemeterUserDataItemValue, DemeterUserDataModel } from '../../../Generated/Raven-Demeter/api';
import NavigationRoutes from '../../../Layouts/NavigationRoutes';
import useApi from '../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import useMultipleApis from '../../Apis/Hooks/useMultipleApisHook';
import addRowIcon from '../../Components/Assets/addRow.svg';
import trashIcon from '../../Components/Assets/trash.svg';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import CurrencyDropdown from '../../Components/Form/Inputs/CurrencyDropdown';
import DatePickerInput from '../../Components/Form/Inputs/DatePickerInput';
import NumberInput from '../../Components/Form/Inputs/NumberInput';
import SelectInput, { SelectInputOption } from '../../Components/Form/Inputs/SelectInput';
import TextInput from '../../Components/Form/Inputs/TextInput';
import UnitOfMeasureGeneralDropdown from '../../Components/Form/Inputs/UnitOfMeasureGeneralDropdown';
import PageHeaderWithBackNavigation from '../../Components/Headers/PageHeaderWithBackNavigation';
import LoadingSpinner from '../../Components/LoadingSpinner/LoadingSpinner';
import ActionModal from '../../Components/Modals/ActionModal/ActionModal';
import formattingService from '../../Services/Formatting/FormattingService';
import useLanguage from '../../Services/Language/useLanguageHook';
import UserDataInputCell, { UserDataInputCellHandle } from './UserDataInputCell';
import pageStyles from './UserDataItemsPage.module.scss';
import styles from './UserDataPage.module.scss';

type RowReference = {
    key: string;
    dateHandle?: UserDataInputCellHandle;
    valueHandle?: UserDataInputCellHandle;
};
const rowsToDisplayOptions = [20, 50, 100, 'All'] as const;

const UserDataItemsPage: FC = () => {
    const navigate = useNavigate();
    const { demeterUserDataGuid } = useParams();
    const [translations] = useLanguage();

    const [userData, setUserData] = useState<DemeterUserDataModel | null>(null);
    const [headerBaseline, setHeaderBaseline] = useState<Pick<DemeterUserDataModel, 'name' | 'currency' | 'unitOfMeasure'> | null>(null);
    const [loaded, setLoaded] = useState(false);

    const rows: DemeterUserDataItemValue[] = userData?.rows ?? [];
    const setRows = (updater: (previousRows: DemeterUserDataItemValue[]) => DemeterUserDataItemValue[]) => {
        setUserData((previousUserData) => {
            if (previousUserData) {
                return { ...previousUserData, rows: updater(previousUserData.rows) };
            }
            return previousUserData;
        });
    };

    const [rowsToDisplay, setRowsToDisplay] = useState<number | 'All'>(20);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
    const [pendingDeleteItem, setPendingDeleteItem] = useState<{ demeterUserDataItemGuid: string; asOfDate: string } | null>(null);

    const [updating, triggerSaveApis] = useMultipleApis(
        () => {
            if (!demeterUserDataGuid || (!hasDirtyUserDataHeader && !rowState.anyDirty) || !userData) {
                return null;
            }

            const apiCalls = [];
            if (hasDirtyUserDataHeader) {
                apiCalls.push(demeterUserDataApi.updateDemeterUserData(demeterUserDataGuid, userData));
            }
            if (rowState.anyDirty) {
                apiCalls.push(demeterUserDataApi.batchUpdateDemeterUserDataItems(demeterUserDataGuid, userData));
            }

            return apiCalls;
        },
        {
            stopAutoExecute: true,
            successMessage: translations.userData.items.messages.saveSuccess,
            errorMessage: translations.userData.items.errors.saveError,
            showResponseErrorMessage: true,
        },
    );

    const [previousUpdating, setPreviousUpdating] = useState(updating);
    useEffect(() => {
        if (previousUpdating && !updating) {
            refreshGetDemeterUserData();
        }
        setPreviousUpdating(updating);
    }, [updating]);

    const [removing, removeRow, , removeRowError] = useApiWithoutAutoExecute(
        (demeterUserDataItemGuid?: string) => {
            if (!demeterUserDataGuid || !demeterUserDataItemGuid) {
                return null;
            }
            return demeterUserDataApi.deleteDemeterUserDataItem(demeterUserDataGuid, demeterUserDataItemGuid, {
                demeterUserDataGuid,
                demeterUserDataItemGuid,
            });
        },
        { stopAutoExecute: true, successMessage: translations.userData.items.messages.itemDeleted },
    );

    const [loadingUserData, refreshGetDemeterUserData, demeterUserDataResponse] = useApi(() => {
        if (!demeterUserDataGuid) {
            return null;
        }
        return demeterUserDataApi.getDemeterUserData(demeterUserDataGuid);
    });

    useEffect(() => {
        if (!demeterUserDataResponse?.demeterUserData) {
            return;
        }

        const model = demeterUserDataResponse.demeterUserData;
        const fetchedRows = [...(model.rows ?? [])]
            .map((row) => ({
                ...row,
                asOfDate: formattingService.toApiDate(row.asOfDate),
            }))
            .sort((a, b) => new Date(b.asOfDate).getTime() - new Date(a.asOfDate).getTime());
        const normalized: DemeterUserDataModel = { ...model, rows: fetchedRows };

        rowReferencesReference.current = [];

        // This is to force a desending reorder without need some unique row IDs.
        setUserData({ ...normalized, rows: [] });
        setTimeout(() => {
            setUserData(normalized);
        }, 0);

        setHeaderBaseline({
            name: normalized.name,
            currency: normalized.currency,
            unitOfMeasure: normalized.unitOfMeasure,
        });
        setLoaded(true);
        if (fetchedRows.length < rowsToDisplayOptions[0]) {
            setRowsToDisplay('All');
        }
    }, [demeterUserDataResponse]);

    type RowReferencesMap = RowReference[];
    const rowReferencesReference = useRef<RowReferencesMap>([]);
    const headerNameReference = useRef<UserDataInputCellHandle | null>(null);
    const nextLocalIdentifierReference = useRef(1);
    const getNewRowId = (): string => {
        const identifier = `local-${nextLocalIdentifierReference.current}`;
        nextLocalIdentifierReference.current += 1;
        return identifier;
    };

    const getRowKey = (row: DemeterUserDataItemValue, index: number): string => {
        if (row.demeterUserDataItemGuid) {
            return row.demeterUserDataItemGuid;
        }
        let reference = rowReferencesReference.current[index];
        if (!reference || !reference.key) {
            reference = { key: getNewRowId() };
            rowReferencesReference.current[index] = reference;
        }
        return reference.key;
    };

    useEffect(() => {
        if (!pendingDeleteItem || removing || removeRowError) {
            return;
        }

        setRows((previousRows) => {
            const removalIndexes: number[] = [];
            previousRows.forEach((row, index) => {
                if (row.asOfDate === pendingDeleteItem.asOfDate) {
                    removalIndexes.push(index);
                }
            });
            const nextRows = previousRows.filter((row, index) => !removalIndexes.includes(index));
            rowReferencesReference.current = rowReferencesReference.current.filter((_, index) => !removalIndexes.includes(index));
            return nextRows;
        });
        setPendingDeleteItem(null);
    }, [pendingDeleteItem, removing, removeRowError]);

    const addRow = () => {
        if (!userData) {
            return;
        }
        const newRow: DemeterUserDataItemValue = {
            demeterUserDataItemGuid: '',
            asOfDate: '',
            value: NaN,
            isActualValue: false,
        };
        const key = getNewRowId();
        rowReferencesReference.current.unshift({ key });
        setRows((previousRows) => [newRow, ...previousRows]);
    };

    const updateCellDate = (index: number, asOfDate: string) => {
        setRows((previousRows) =>
            previousRows.map((row, currentIndex) => {
                if (currentIndex === index) {
                    return { ...row, asOfDate };
                }
                return row;
            }),
        );
    };

    const updateCellValue = (index: number, value: number) => {
        setRows((previousRows) =>
            previousRows.map((row, currentIndex) => {
                if (currentIndex === index) {
                    return { ...row, value };
                }
                return row;
            }),
        );
    };

    const hasDirtyUserDataHeader = useMemo(() => {
        if (!userData || !headerBaseline) {
            return false;
        }
        return (['name', 'currency', 'unitOfMeasure'] as const).some((key) => userData[key] !== headerBaseline[key]);
    }, [userData, headerBaseline]);

    const rowState = useMemo(() => {
        const seen = new Set<string>();
        const duplicates = new Set<string>();
        rows.forEach((row) => {
            const key = formattingService.toShortDayMonthYear(row.asOfDate);
            if (key) {
                if (seen.has(key)) {
                    duplicates.add(key);
                } else {
                    seen.add(key);
                }
            }
        });

        const references = rowReferencesReference.current.slice(0, rows.length);
        const handles: UserDataInputCellHandle[] = references.flatMap(
            (reference) => [reference?.dateHandle, reference?.valueHandle].filter(Boolean) as UserDataInputCellHandle[],
        );
        const anyDirty = handles.some((handle) => handle.isDirty());
        const inputsValid = duplicates.size === 0 && handles.every((handle) => handle.isValid());
        return { duplicates, anyDirty, inputsValid };
    }, [rows]);

    const [headerFieldsChanged, setHeaderFieldsChanged] = useState(0);

    const headerFieldsValid = useMemo(() => {
        if (!headerNameReference.current) {
            return true;
        }
        return headerNameReference.current.isValid();
    }, [headerFieldsChanged, userData?.name]);

    const canSave = !!userData && (hasDirtyUserDataHeader || rowState.anyDirty) && rowState.inputsValid && headerFieldsValid;
    const isInitialLoad = !loaded || loadingUserData || !userData;

    const rowsToRender = useMemo(() => {
        if (rowsToDisplay === 'All') {
            return rows;
        }
        const limit = typeof rowsToDisplay === 'number' ? rowsToDisplay : rows.length;
        return rows.slice(0, limit);
    }, [rows, rowsToDisplay]);

    const getRowsToDisplayText = (rowsToDisplayValue: number | 'All', totalRows: number): string => {
        const allRowsTemplate = translations.userData.items.dataitems.displayingAllRows;
        const mostRecentTemplate = translations.userData.items.dataitems.displayingMostRecent;

        if (rowsToDisplayValue === 'All') {
            if (allRowsTemplate) {
                if (allRowsTemplate.includes('{count}')) {
                    return allRowsTemplate.replace('{count}', String(totalRows));
                }
                return allRowsTemplate;
            }
            return `Displaying all ${totalRows} rows`;
        }

        const displayed = Math.min(typeof rowsToDisplayValue === 'number' ? rowsToDisplayValue : totalRows, totalRows);
        if (mostRecentTemplate) {
            if (mostRecentTemplate.includes('{displayed}') || mostRecentTemplate.includes('{total}')) {
                return mostRecentTemplate.replace('{displayed}', String(displayed)).replace('{total}', String(totalRows));
            }
            return mostRecentTemplate;
        }
        return `Displaying most recent ${displayed} of ${totalRows}`;
    };

    const rowsToDisplaySelectOptions: SelectInputOption<number | 'All'>[] = useMemo(
        () => rowsToDisplayOptions.map((option) => ({ label: String(option), value: option })),
        [],
    );

    const renderDateCell = (row: DemeterUserDataItemValue, index: number) => {
        const rowId = getRowKey(row, index);
        return (
            <div className={`${pageStyles.user_data_items__cell} ${pageStyles.user_data_items_table_cell_date}`}>
                <UserDataInputCell
                    ref={(element: UserDataInputCellHandle | null) => {
                        const existing = rowReferencesReference.current[index] ?? { key: getRowKey(row, index) };
                        if (element) {
                            existing.dateHandle = element;
                        } else {
                            delete existing.dateHandle;
                        }
                        rowReferencesReference.current[index] = existing;
                    }}
                    value={row.asOfDate}
                    required
                    isDuplicate={rowState.duplicates.has(formattingService.toShortDayMonthYear(row.asOfDate))}
                    onStateChange={() => setRows((currentRows) => currentRows.slice())}
                >
                    <DatePickerInput
                        name={`asOfDate-${rowId}`}
                        testId={`asOfDate-${rowId}`}
                        handleDateChange={(date) => {
                            updateCellDate(index, formattingService.toApiDate(date || ''));
                        }}
                        track={{ eventCategory: 'UserData', eventAction: 'ItemDate' }}
                    />
                </UserDataInputCell>
            </div>
        );
    };

    const renderValueCell = (row: DemeterUserDataItemValue, index: number) => {
        const rowId = getRowKey(row, index);
        return (
            <div className={`${pageStyles.user_data_items__cell} ${pageStyles.user_data_items_table__cell_value}`}>
                <UserDataInputCell
                    ref={(element: UserDataInputCellHandle | null) => {
                        const existing = rowReferencesReference.current[index] ?? { key: getRowKey(row, index) };
                        if (element) {
                            existing.valueHandle = element;
                        } else {
                            delete existing.valueHandle;
                        }
                        rowReferencesReference.current[index] = existing;
                    }}
                    value={row.value}
                    required
                    onStateChange={() => setRows((currentRows) => currentRows.slice())}
                >
                    <NumberInput
                        className={pageStyles.user_data_items_input_cell_wrapper}
                        testId={`value-${rowId}`}
                        handleTextChange={(value: number) => updateCellValue(index, value)}
                        track={{ eventCategory: 'UserData', eventAction: 'ItemValueChanged' }}
                    />
                </UserDataInputCell>
            </div>
        );
    };

    const renderActionsCell = (row: DemeterUserDataItemValue, index: number) => (
        <div className={`${pageStyles.user_data_items__cell} ${pageStyles.user_data_items_table_cell_actions}`}>
            <div className={pageStyles.user_data_items_table_actions_container}>
                <button
                    type="button"
                    onClick={() => {
                        if (!row.demeterUserDataItemGuid) {
                            setRows((previousRows) => previousRows.filter((_, previousRowsIndex) => previousRowsIndex !== index));
                            rowReferencesReference.current = rowReferencesReference.current.filter((_, rowReferencesIndex) => rowReferencesIndex !== index);
                        } else {
                            setPendingDeleteItem({
                                demeterUserDataItemGuid: row.demeterUserDataItemGuid,
                                asOfDate: row.asOfDate,
                            });
                            removeRow(row.demeterUserDataItemGuid);
                        }
                    }}
                    title={translations.actions.delete}
                    className={pageStyles.user_data_items_action_button}
                    disabled={
                        updating ||
                        (!!row.demeterUserDataItemGuid && !!pendingDeleteItem && pendingDeleteItem.demeterUserDataItemGuid === row.demeterUserDataItemGuid)
                    }
                >
                    <img src={trashIcon} alt={translations.actions.delete} />
                </button>
            </div>
        </div>
    );

    const renderRow = (row: DemeterUserDataItemValue, index: number) => {
        const rowId = getRowKey(row, index);
        return (
            <div key={rowId} className={pageStyles.user_data_items__row}>
                {renderDateCell(row, index)}
                {renderValueCell(row, index)}
                {renderActionsCell(row, index)}
            </div>
        );
    };

    const handleBack = () => {
        const hasChanges = hasDirtyUserDataHeader || rowState.anyDirty;
        if (hasChanges) {
            setShowUnsavedChangesModal(true);
            return;
        }
        navigate(NavigationRoutes.UserData);
    };

    const handleDiscardChanges = () => {
        setShowUnsavedChangesModal(false);
        navigate(NavigationRoutes.UserData);
    };

    const handleReturnToPage = () => setShowUnsavedChangesModal(false);

    const handleSave = () => {
        if (canSave) {
            triggerSaveApis();
        }
    };

    if (isInitialLoad) {
        return <LoadingSpinner />;
    }

    return (
        <>
            {updating && <LoadingSpinner />}
            <div className={updating ? pageStyles.user_data_items__content_hidden : styles.master_page_container}>
                <div className={pageStyles.user_data_items}>
                    <div className={pageStyles.user_data_items__header_row}>
                        <PageHeaderWithBackNavigation
                            title={userData?.name || translations.userData.items.headers.header}
                            handleBackNavigation={handleBack}
                            track={{ eventCategory: 'UserData', eventAction: 'BackClicked' }}
                        />
                        <LinkButton
                            title={translations.actions.save}
                            type={LinkButtonType.Blue}
                            onClick={handleSave}
                            disabled={!canSave || updating}
                            track={{ eventCategory: 'UserData', eventAction: 'SaveClicked' }}
                        />
                    </div>

                    <div className={styles.user_data_management_container}>
                        <div className={pageStyles.user_data_items__section}>
                            <div className={`${pageStyles.user_data_items__form_grid} ${pageStyles.user_data_items__top_spacer_same_as_input}`}>
                                <div className={pageStyles.user_data_items__form_grid_item}>
                                    <UserDataInputCell
                                        ref={headerNameReference}
                                        value={userData?.name}
                                        required
                                        childIsValid={!!userData?.name && userData.name.trim() !== ''}
                                        onStateChange={() => setHeaderFieldsChanged((previous) => previous + 1)}
                                        errorMessage={translations.companies.errors.requiredField}
                                    >
                                        <TextInput
                                            title={translations.userData.items.headers.priceSeriesName}
                                            value={userData?.name}
                                            handleTextChange={(value) => {
                                                setUserData((previous) => (previous ? { ...previous, name: value } : previous));
                                            }}
                                            required
                                            disabled={updating}
                                            track={{ eventCategory: 'UserData', eventAction: 'PriceSeriesNameChanged', eventValue: userData?.name }}
                                        />
                                    </UserDataInputCell>
                                </div>
                                <div className={pageStyles.user_data_items__form_grid_item}>
                                    <UserDataInputCell value={userData?.unitOfMeasure}>
                                        <UnitOfMeasureGeneralDropdown
                                            label={translations.words.unit}
                                            unitOfMeasure={userData?.unitOfMeasure}
                                            handleChange={(value) => setUserData((previous) => ({ ...previous!, unitOfMeasure: value! }))}
                                            disabled={updating}
                                            track={{ eventCategory: 'UserData', eventAction: 'UnitOfMeasureChanged', eventValue: userData?.unitOfMeasure }}
                                        />
                                    </UserDataInputCell>
                                </div>
                                <div className={pageStyles.user_data_items__form_grid_item}>
                                    <UserDataInputCell value={userData?.currency}>
                                        <CurrencyDropdown
                                            label={translations.words.currency}
                                            currency={userData?.currency}
                                            handleChange={(value) => setUserData((previous) => ({ ...previous!, currency: value! }))}
                                            disabled={updating}
                                            track={{ eventCategory: 'UserData', eventAction: 'CurrencyChanged' }}
                                        />
                                    </UserDataInputCell>
                                </div>
                            </div>
                        </div>

                        <div className={pageStyles.user_data_items__section}>
                            <div className={pageStyles.user_data_items__divider} />

                            <div className={pageStyles.user_data_items__rows_to_display_controls}>
                                <div>
                                    <SelectInput
                                        title={translations.userData.management.table.itemsPerPage}
                                        value={rowsToDisplay}
                                        options={rowsToDisplaySelectOptions}
                                        handleOptionChange={(value) => setRowsToDisplay(value)}
                                        disabled={updating}
                                        track={{ eventCategory: 'UserData', eventAction: 'ItemsRowsToDisplayChanged', eventValue: rowsToDisplay }}
                                    />
                                </div>

                                <div className={pageStyles.user_data_items__rows_to_display_text}>{getRowsToDisplayText(rowsToDisplay, rows.length)}</div>
                            </div>

                            <div className={pageStyles.user_data_items__add_row_container}>
                                <LinkButton
                                    title={translations.userData.items.actions.addRow}
                                    type={LinkButtonType.White}
                                    onClick={addRow}
                                    disabled={updating}
                                    icon={<img src={addRowIcon} alt="" aria-hidden="true" />}
                                    track={{ eventCategory: 'UserData', eventAction: 'AddRowClicked' }}
                                />
                            </div>

                            <div className={pageStyles.user_data_items__table}>
                                <div className={pageStyles.user_data_items__row}>
                                    <div className={`${pageStyles.user_data_items__header_cell} ${pageStyles.user_data_items_table_cell_date}`}>
                                        {translations.text.asOfDate}
                                    </div>
                                    <div className={`${pageStyles.user_data_items__header_cell} ${pageStyles.user_data_items_table_cell_value}`}>
                                        {translations.words.values}
                                    </div>
                                    <div className={`${pageStyles.user_data_items__header_cell} ${pageStyles.user_data_items_table_cell_actions}`}>
                                        {translations.words.actions}
                                    </div>
                                </div>

                                <div className={pageStyles.user_data_items__body}>{rowsToRender.map((row, index) => renderRow(row, index))}</div>
                            </div>

                            <div className={pageStyles.user_data_items__table_footer_note}>{translations.userData.items.dataitems.additionalDatesSorted}</div>
                        </div>
                    </div>
                </div>

                <ActionModal
                    header={translations.userData.items.messages.unsavedChangesHeader}
                    showModal={showUnsavedChangesModal}
                    actionButtonName={translations.userData.items.messages.discardChanges}
                    secondaryActionButtonName={translations.userData.items.messages.returnToPage}
                    handleAction={handleDiscardChanges}
                    handleSecondaryAction={handleReturnToPage}
                    handleCancel={handleReturnToPage}
                    hideCancel
                    track={{ eventCategory: 'UserData', eventAction: 'ModalDiscardChanges' }}
                >
                    <div className={pageStyles.modal_content}>{translations.text.leaveWithoutSaving}</div>
                </ActionModal>
            </div>
        </>
    );
};

export default UserDataItemsPage;
